import React, { useEffect } from "react";
import SelectOption from "components/common/Select/Select";
import WorkerShiftCustomSelect from "./asyncSelect";
import MultiSelectDropdown from "components/common/MultiSelectDropdown";
import {
  fetchOptionsForWorkerAndParticpant,
  parseBooleanVal,
} from "Utils/utils";
import _ from "lodash";
import useCareLabels from "hooks/useCareLabels";

const ParticipantSelection = (props) => {
  const { participantLabel } = useCareLabels();
  const {
    watchFormData,
    calendarView,
    participantOptions,
    register,
    setValue,
    selectedShift,
    checkRunningShiftStatus,
    checkDisabilityOfShiftStatus,
    handleChangeWorkerOrParticipant,
    isFetchRecordDetailLoader,
    control,
    watch,
    setSelectedOptions,
    selectedOptions,
    clearErrors,
    errors,
    userItem,
    setIsWorkerLoaded,
    isCallFromAddShift,
    setWorkerOptions,
    setIsParticipantLoaded,
    setParticipantOptions,
    isParticipantLoaded,
    selectedParticipant,
    handleChangeServiceCategory,
    createOrUpdateWorkerShiftLoader,
    getSelectedOption,
    getSelectedParticipant,
    isOpenShift = false,
    shiftDetailData,
    setShouldCallHandleChangeWorkerOrParticipant,
    serviceTypeSelectedOptions,
    prefillFromShiftRequest = null,
  } = props;

  const lockParticipantFromCalendarCell =
    Boolean(userItem) &&
    calendarView === "participant-view" &&
    selectedShift === null;

  const mapPtToSelectOption = (p) => {
    if (!p) return null;
    const uuid = p.uuid || p.value;
    if (!uuid) return null;
    return {
      ...p,
      uuid,
      value: uuid,
      label:
        p.label ||
        `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
        "Participant",
    };
  };

  const shiftRequestParticipantFallback = (() => {
    if (Array.isArray(watchFormData?.pt_id)) return null;
    const ptId = watchFormData?.pt_id;
    if (!ptId) return null;

    const fromOptions = participantOptions?.find(
      (x) =>
        x?.value === ptId ||
        x?.uuid === ptId ||
        String(x?.value) === String(ptId) ||
        String(x?.uuid) === String(ptId)
    );
    if (fromOptions) return fromOptions;

    if (
      userItem &&
      String(userItem.uuid || userItem.value) === String(ptId)
    ) {
      return mapPtToSelectOption(userItem);
    }

    const fromRequest = prefillFromShiftRequest?.participants?.find(
      (p) => String(p?.uuid || p?.value) === String(ptId)
    );
    return mapPtToSelectOption(fromRequest);
  })();

  const isGroupOpenShiftFlag =
    parseBooleanVal(shiftDetailData?.open_group_shift) ||
    selectedShift?.open_group_shift;
  const groupParticipantCount = Array.isArray(shiftDetailData?.participants)
    ? shiftDetailData.participants.length
    : Array.isArray(watchFormData?.pt_id)
    ? watchFormData.pt_id.length
    : 0;
  const isEditingMultiParticipantGroup =
    selectedShift !== null &&
    (parseBooleanVal(watchFormData?.is_group_shift) ||
      parseBooleanVal(shiftDetailData?.is_group_shift) ||
      parseBooleanVal(selectedShift?.is_group_shift) ||
      isGroupOpenShiftFlag) &&
    (isGroupOpenShiftFlag || groupParticipantCount > 1);

  useEffect(() => {
    if (prefillFromShiftRequest) {
      return;
    }
    if (
      selectedParticipant &&
      !_.isArray(selectedParticipant) &&
      !createOrUpdateWorkerShiftLoader &&
      selectedShift === null &&
      watchFormData?.pt_id &&
      selectedParticipant?.invoice_category_id
    ) {
      // Apply participant default when present (V360-5366). Skip when empty so
      // selecting a participant does not wipe a manually chosen category.
      const ptCategoryId = selectedParticipant?.invoice_category_id;
      if (ptCategoryId == null || ptCategoryId === "") {
        return;
      }
      handleChangeServiceCategory(ptCategoryId);
    } else if (selectedParticipant && _.isArray(selectedParticipant)) {
      // handleChangeServiceCategory(null);
    }
    // Use primitive participant identity — not the options object — so option
    // rehydration does not wipe a manually chosen NDIS line item.
  }, [
    Array.isArray(selectedParticipant)
      ? null
      : selectedParticipant?.uuid || selectedParticipant?.value,
    Array.isArray(selectedParticipant)
      ? null
      : selectedParticipant?.invoice_category_id,
    watchFormData?.pt_id,
    selectedShift,
    createOrUpdateWorkerShiftLoader,
    prefillFromShiftRequest,
  ]);

  useEffect(() => {
    const isGroupOpenShift =
      parseBooleanVal(shiftDetailData?.open_group_shift) ||
      selectedShift?.open_group_shift;
    const isGroupShiftForm = parseBooleanVal(watchFormData?.is_group_shift);
    const isGroupShiftFromOriginal =
      parseBooleanVal(shiftDetailData?.is_group_shift) ||
      parseBooleanVal(selectedShift?.is_group_shift);
    // Keep original-shift flags while hydrating (V360-5361). After the user
    // turns Group Shift off in worker-view edit, follow the form so leftover
    // multi-select arrays can collapse for the single-select.
    const shiftDetailHydrated =
      shiftDetailData &&
      typeof shiftDetailData === "object" &&
      !Array.isArray(shiftDetailData) &&
      Object.prototype.hasOwnProperty.call(shiftDetailData, "is_group_shift");
    const userTurnedGroupShiftOffInWorkerEdit =
      calendarView === "worker-view" &&
      selectedShift !== null &&
      shiftDetailHydrated &&
      !isGroupShiftForm &&
      isGroupShiftFromOriginal;
    const isGroupShift =
      isGroupShiftForm ||
      (!userTurnedGroupShiftOffInWorkerEdit && isGroupShiftFromOriginal);

    if (
      ((!isGroupShift && !isGroupOpenShift) ||
        userTurnedGroupShiftOffInWorkerEdit) &&
      Array.isArray(watchFormData?.pt_id)
    ) {
      const firstPt = watchFormData.pt_id[0];
      setValue("pt_id", firstPt?.uuid || firstPt?.value || firstPt || null);
      if (setSelectedOptions) {
        setSelectedOptions([]);
      }
      return;
    }

    // Participant-view + calendar cell (create): lock the row participant.
    // Editing an existing multi-participant / open group shift must keep the array.
    if (
      calendarView === "participant-view" &&
      isGroupShift &&
      userItem &&
      Array.isArray(watchFormData?.pt_id) &&
      !isEditingMultiParticipantGroup
    ) {
      const lockedUuid =
        userItem?.uuid ||
        watchFormData.pt_id[0]?.uuid ||
        watchFormData.pt_id[0]?.value ||
        watchFormData.pt_id[0];
      if (lockedUuid) {
        setValue("pt_id", lockedUuid);
      }
      return;
    }

    // Group MultiSelect needs an array; scalar pt_id breaks sync
    // (worker-view always; participant-view Add Shift / vacant when unlocked).
    if (
      (calendarView === "worker-view" ||
        (calendarView === "participant-view" &&
          (!userItem || isEditingMultiParticipantGroup))) &&
      isGroupShiftForm &&
      watchFormData?.pt_id &&
      !Array.isArray(watchFormData.pt_id)
    ) {
      const match = participantOptions?.find(
        (x) =>
          x.value === watchFormData.pt_id || x.uuid === watchFormData.pt_id
      );
      setValue(
        "pt_id",
        match
          ? [{ ...match }]
          : [
              {
                uuid: watchFormData.pt_id,
                value: watchFormData.pt_id,
              },
            ]
      );
    }
  }, [
    calendarView,
    userItem?.uuid,
    watchFormData?.is_group_shift,
    watchFormData?.pt_id,
    shiftDetailData?.open_group_shift,
    shiftDetailData?.is_group_shift,
    selectedShift?.open_group_shift,
    selectedShift?.is_group_shift,
    selectedShift,
    shiftDetailData?.participants,
    isEditingMultiParticipantGroup,
    participantOptions,
    setValue,
    setSelectedOptions,
  ]);

  return (
    <div className="">
      <label className="text-gray-700 text-xs font-medium mb-1">
        {participantLabel}
      </label>

      {/* Group shift with worker view*/}
      {watchFormData?.is_group_shift && calendarView === "worker-view" && (
        <MultiSelectDropdown
          showIcon
          data={participantOptions}
          cssClass="z-30 capitalize"
          name="pt_id"
          register={register}
          setValue={setValue}
          isDisabled={
            checkRunningShiftStatus(selectedShift?.status) ||
            isFetchRecordDetailLoader
            // || selectedShift?.is_group_shift
          }
          control={control}
          watch={watch}
          selectedOption={getSelectedParticipant("pt_id")}
          setSelectedOptions={setSelectedOptions}
          selectedOptions={selectedOptions}
          handleInputChange={(e) => {
            if (!e || !watchFormData?.is_group_shift) return;
            const UUIDs = e.map((el) => el?.uuid || el?.value).filter(Boolean);
            // Edit: refresh listing/tasks for the multi-select change.
            if (!isCallFromAddShift && selectedShift !== null) {
              setShouldCallHandleChangeWorkerOrParticipant(false);
              handleChangeWorkerOrParticipant({
                type: "participant",
                uuid: UUIDs,
                id: null,
                isChanged: true,
              });
              return;
            }
            // Create (incl. open group shift): fetch tasks for every selected
            // participant as they are added — same as edit single→group (V360-5369).
            if (selectedShift === null) {
              handleChangeWorkerOrParticipant({
                type: "participant",
                uuid: UUIDs,
                isChanged: true,
              });
            }
          }}
          clearErrors={clearErrors}
          errorMessage={
            errors?.pt_id?.type === "required"
              ? "This field is required!"
              : null
          }
          calendarView={calendarView === "worker-view"}
        />
      )}

      {/* Participant-view + group: lock the row participant on create from a
          calendar cell. Editing a multi-participant / open group shift uses
          the multi-select below so all participants remain visible. */}
      {calendarView === "participant-view" &&
        watchFormData?.is_group_shift &&
        userItem &&
        !isEditingMultiParticipantGroup && (
          <SelectOption
            key={`participant-group-locked-${userItem.uuid}`}
            noOptionsMessage={`No ${participantLabel} Assigned`}
            isRequired={true}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            name="pt_id"
            loading={isFetchRecordDetailLoader || isParticipantLoaded}
            isDisabled={true}
            cssClass="z-30"
            showIcon={true}
            isSearch={false}
            isClearable={false}
            data={participantOptions}
            isForRostering={true}
            selectedOption={
              participantOptions.find(
                (x) =>
                  x.value === userItem.uuid ||
                  x.uuid === userItem.uuid ||
                  x.value === watchFormData?.pt_id ||
                  x.uuid === watchFormData?.pt_id
              ) || {
                label: `${userItem.first_name || ""} ${
                  userItem.last_name || ""
                }`.trim(),
                value: userItem.uuid,
                uuid: userItem.uuid,
              }
            }
            errorMessage={
              errors?.pt_id?.type === "required"
                ? "This field is required!"
                : null
            }
          />
        )}

      {/* Participant-view + group: multi-select when not locked to a calendar cell */}
      {calendarView === "participant-view" &&
        watchFormData?.is_group_shift &&
        (!userItem || isEditingMultiParticipantGroup) && (
          <MultiSelectDropdown
            showIcon
            data={participantOptions}
            cssClass="z-30"
            name="pt_id"
            register={register}
            setValue={setValue}
            isDisabled={
              checkRunningShiftStatus(selectedShift?.status) ||
              isFetchRecordDetailLoader ||
              lockParticipantFromCalendarCell
            }
            control={control}
            watch={watch}
            selectedOption={getSelectedParticipant("pt_id")}
            setSelectedOptions={setSelectedOptions}
            selectedOptions={selectedOptions}
            clearErrors={clearErrors}
            handleInputChange={(e) => {
              if (lockParticipantFromCalendarCell) return;
              if (isCallFromAddShift && e && selectedShift === null) {
                const UUIDs = e?.map((el) => el?.uuid);
                handleChangeWorkerOrParticipant({
                  type: "participant",
                  uuid: UUIDs,
                });
              }
            }}
            errorMessage={
              errors?.pt_id?.type === "required"
                ? "This field is required!"
                : null
            }
            calendarView={calendarView === "participant-view"}
          />
        )}

      {/* Not Group shift with worker view*/}
      {!watchFormData?.is_group_shift && calendarView === "worker-view" && (
        <SelectOption
          key={`participant-single-${watchFormData?.is_group_shift}`}
          persistSelectedOption={
            selectedShift !== null &&
            parseBooleanVal(
              selectedShift?.is_group_shift || shiftDetailData?.is_group_shift
            )
          }
          preserveSelectionWhenOptionMissing={
            selectedShift !== null &&
            parseBooleanVal(
              selectedShift?.is_group_shift || shiftDetailData?.is_group_shift
            )
          }
          isClearable={selectedShift === null || isCallFromAddShift}
          isClearCall={async () => {
            if (selectedShift !== null && !isCallFromAddShift) return;
            if (watchFormData?.is_group_shift) {
              setValue("is_group_shift", false);
            }
            // Worker-view: keep the (auto) selected worker — only clear participant.
            setValue("pt_id", null);
            setIsParticipantLoaded(true);
            fetchOptionsForWorkerAndParticpant("participant")
              .then((response) => {
                // Sort participants alphabetically before setting
                const sorted = [...response].sort((a, b) => {
                  const aLabel =
                    a.label ||
                    `${a.first_name || ""} ${a.last_name || ""}`.trim();
                  const bLabel =
                    b.label ||
                    `${b.first_name || ""} ${b.last_name || ""}`.trim();
                  return aLabel.localeCompare(bLabel);
                });
                setParticipantOptions(sorted);
                setIsParticipantLoaded(false);
              })
              .catch(() => setIsParticipantLoaded(false));
          }}
          noOptionsMessage={`No ${participantLabel} Assigned`}
          isRequired={true}
          register={register}
          setValue={setValue}
          clearErrors={clearErrors}
          name="pt_id"
          loading={isFetchRecordDetailLoader || isParticipantLoaded}
          isDisabled={
            checkRunningShiftStatus(selectedShift?.status) ||
            isFetchRecordDetailLoader ||
            isParticipantLoaded ||
            (userItem && calendarView === "participant-view" ? true : false)
          }
          isValueChanges={(e, val) => {
            const isNotEmptyWorkerId =
              watchFormData?.worker_id !== null &&
              watchFormData?.worker_id !== "" &&
              watchFormData?.worker_id !== undefined;
            // Edit: always refresh listing/tasks for the newly selected participant
            // (including switching back to the original — V360-5365).
            if (selectedShift !== null && e) {
              handleChangeWorkerOrParticipant({
                type: "participant",
                uuid: val,
                isChanged: true,
              }).catch(() => {});
              return;
            }
            if (isNotEmptyWorkerId && isCallFromAddShift) {
              setIsWorkerLoaded(true);
              // Keep worker selection; handler preserves worker in refreshed options.
              handleChangeWorkerOrParticipant(
                {
                  type: "participant",
                  uuid: val,
                },
                { isCheckWorkerExist: true }
              )
                .then(() => setIsWorkerLoaded(false))
                .catch(() => setIsWorkerLoaded(false));
              return;
            }
            if (userItem === null && e) {
              if (!isNotEmptyWorkerId) {
                setWorkerOptions([]);
                setValue("worker_id", null);
              }
              setIsWorkerLoaded(true);
              handleChangeWorkerOrParticipant({
                type: "participant",
                uuid: val,
              })
                .then(() => setIsWorkerLoaded(false))
                .catch(() => setIsWorkerLoaded(false));
            }
          }}
          cssClass="z-30"
          showIcon={true}
          isSearch={true}
          data={participantOptions}
          selectedOption={
            Array.isArray(watchFormData?.pt_id)
              ? participantOptions.find((x) => {
                  const first = watchFormData.pt_id[0];
                  const ptUuid = first?.uuid || first?.value || first;
                  return (
                    x.value === ptUuid ||
                    x.uuid === ptUuid ||
                    String(x.value) === String(ptUuid)
                  );
                }) || null
              : watchFormData?.pt_id
              ? participantOptions.find((x) => x.value === watchFormData?.pt_id)
              : null
          }
          errorMessage={
            errors?.pt_id?.type === "required"
              ? "This field is required!"
              : null
          }
          isForRostering={true}
        />
      )}

      {/* Participant View — single participant only when group shift is off */}
      {calendarView === "participant-view" &&
        !watchFormData?.is_group_shift &&
        (isCallFromAddShift ? (
          <>
            {/* Add Shift — async select (many-to-many rostering) */}
            <select
              className="hidden"
              {...register("pt_id", {
                value: watchFormData?.pt_id || null,
                required: true,
              })}
            />
            <WorkerShiftCustomSelect
              serviceTypeSelectedOptions={serviceTypeSelectedOptions}
              isClearable={
                !lockParticipantFromCalendarCell && isCallFromAddShift
              }
              calendarView={calendarView}
              loading={isParticipantLoaded}
              isClearCall={async () => {
                if (!isCallFromAddShift) return;
                if (watchFormData?.is_group_shift) {
                  setValue("is_group_shift", false);
                }
                setValue("worker_id", null);
                setValue("pt_id", null);
                setIsWorkerLoaded(true);
                setIsParticipantLoaded(true);
                const serviceTypes = Array.isArray(serviceTypeSelectedOptions)
                  ? serviceTypeSelectedOptions
                  : serviceTypeSelectedOptions
                    ? [serviceTypeSelectedOptions]
                    : [];
                fetchOptionsForWorkerAndParticpant("worker")
                  .then((response) => {
                    setWorkerOptions([...response]);
                    setIsWorkerLoaded(false);
                  })
                  .catch(() => setIsWorkerLoaded(false));
                fetchOptionsForWorkerAndParticpant("participant", serviceTypes)
                  .then((response) => {
                    setParticipantOptions([...response]);
                    setIsParticipantLoaded(false);
                  })
                  .catch(() => setIsParticipantLoaded(false));
              }}
              isOptionRefreshed={(e = []) => {
                // Sort participants alphabetically before setting
                const sorted = [...e].sort((a, b) => {
                  const aLabel =
                    a.label ||
                    `${a.first_name || ""} ${a.last_name || ""}`.trim();
                  const bLabel =
                    b.label ||
                    `${b.first_name || ""} ${b.last_name || ""}`.trim();
                  return aLabel.localeCompare(bLabel);
                });
                setParticipantOptions((prev) => {
                  const merged = [...sorted];
                  const ensure = (item) => {
                    const opt = mapPtToSelectOption(item);
                    if (!opt) return;
                    const exists = merged.some(
                      (p) =>
                        String(p.uuid) === String(opt.uuid) ||
                        String(p.value) === String(opt.value)
                    );
                    if (!exists) merged.push(opt);
                  };
                  // Keep currently selected / shift-request participants if API
                  // list omitted them (service-support filter, inactive, etc.).
                  if (watchFormData?.pt_id && !Array.isArray(watchFormData.pt_id)) {
                    const keep =
                      (Array.isArray(prev) ? prev : []).find(
                        (p) =>
                          String(p.uuid) === String(watchFormData.pt_id) ||
                          String(p.value) === String(watchFormData.pt_id)
                      ) ||
                      shiftRequestParticipantFallback;
                    ensure(keep);
                  }
                  (prefillFromShiftRequest?.participants || []).forEach(ensure);
                  return merged.sort((a, b) => {
                    const aLabel =
                      a.label ||
                      `${a.first_name || ""} ${a.last_name || ""}`.trim();
                    const bLabel =
                      b.label ||
                      `${b.first_name || ""} ${b.last_name || ""}`.trim();
                    return aLabel.localeCompare(bLabel);
                  });
                });
              }}
              clearErrors={clearErrors}
              name="pt_id"
              setValue={setValue}
              defaultOptions={
                watchFormData?.worker_id ||
                prefillFromShiftRequest ||
                (Array.isArray(participantOptions) &&
                  participantOptions.length > 0)
                  ? participantOptions
                  : null
              }
              selectedFallbackOption={shiftRequestParticipantFallback}
              isDisabled={
                lockParticipantFromCalendarCell ||
                isParticipantLoaded ||
                (watchFormData?.is_group_shift &&
                calendarView === "worker-view"
                  ? checkRunningShiftStatus(selectedShift?.status) ||
                    isFetchRecordDetailLoader
                  : checkDisabilityOfShiftStatus(selectedShift?.status) ||
                    isFetchRecordDetailLoader) ||
                (userItem && calendarView === "participant-view") ||
                selectedShift !== null
              }
              selectedValue={watchFormData?.pt_id}
              errorMessage={
                errors?.pt_id?.type === "required"
                  ? "This field is required!"
                  : null
              }
              isValueChanges={(e, val) => {
                if (lockParticipantFromCalendarCell) return;
                const clearWorkerAndFetchData = async () => {
                  setWorkerOptions([]);
                  setValue("worker_id", null);
                  setIsParticipantLoaded(true);
                  setIsWorkerLoaded(true);
                  handleChangeWorkerOrParticipant({
                    type: "participant",
                    uuid: val?.uuid || val,
                  })
                    .then(() => {
                      setIsWorkerLoaded(false);
                      setIsParticipantLoaded(false);
                    })
                    .catch(() => {
                      setIsWorkerLoaded(false);
                      setIsParticipantLoaded(false);
                    });
                };
                if (
                  isCallFromAddShift &&
                  watchFormData?.worker_id !== null &&
                  watchFormData?.worker_id !== undefined &&
                  watchFormData?.worker_id !== ""
                ) {
                  // Group shift: workers are multi-select / shared — refresh listing.
                  // Non-group: keep the already-selected worker (do not clear).
                  if (parseBooleanVal(watchFormData?.is_group_shift)) {
                    clearWorkerAndFetchData().then();
                    return;
                  }

                  setIsParticipantLoaded(true);
                  handleChangeWorkerOrParticipant(
                    {
                      type: "participant",
                      uuid: val?.uuid || val,
                    },
                    { isCheckWorkerExist: true }
                  )
                    .then(() => {
                      setIsParticipantLoaded(false);
                    })
                    .catch(() => {
                      setIsParticipantLoaded(false);
                    });
                  return;
                }

                if (userItem === null && e && (val?.uuid || val)) {
                  clearWorkerAndFetchData().then();
                }
              }}
            />
          </>
        ) : (
          <SelectOption
            isClearable={
              selectedShift === null &&
              !lockParticipantFromCalendarCell &&
              !(userItem && calendarView === "participant-view")
            }
            isClearCall={async () => {
              if (
                lockParticipantFromCalendarCell ||
                (userItem && calendarView === "participant-view")
              ) {
                return;
              }
              setValue("pt_id", null);
              setValue("worker_id", null);
              setIsWorkerLoaded(true);
              setIsParticipantLoaded(true);
              try {
                const [workers, participants] = await Promise.all([
                  fetchOptionsForWorkerAndParticpant("worker"),
                  fetchOptionsForWorkerAndParticpant("participant"),
                ]);
                setWorkerOptions([...workers]);
                const sorted = [...participants].sort((a, b) => {
                  const aLabel =
                    a.label ||
                    `${a.first_name || ""} ${a.last_name || ""}`.trim();
                  const bLabel =
                    b.label ||
                    `${b.first_name || ""} ${b.last_name || ""}`.trim();
                  return aLabel.localeCompare(bLabel);
                });
                setParticipantOptions(sorted);
              } finally {
                setIsWorkerLoaded(false);
                setIsParticipantLoaded(false);
              }
            }}
            key={`participant-single-${watchFormData?.is_group_shift}-${isOpenShift}`}
            isValueChanges={(e, val) => {
              if (
                lockParticipantFromCalendarCell ||
                (userItem && calendarView === "participant-view")
              ) {
                return;
              }
              if (selectedShift !== null && e) {
                handleChangeWorkerOrParticipant({
                  type: "participant",
                  uuid: val,
                  isChanged: true,
                }).catch(() => {});
                return;
              }
              if (userItem === null && e) {
                setWorkerOptions([]);
                setValue("worker_id", null);
                handleChangeWorkerOrParticipant({
                  type: "participant",
                  uuid: val,
                }).then(() => setIsParticipantLoaded(true));
              }
            }}
            noOptionsMessage={`No ${participantLabel} Assigned`}
            isRequired={true}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            name="pt_id"
            loading={isFetchRecordDetailLoader}
            isDisabled={
              checkDisabilityOfShiftStatus(selectedShift?.status) ||
              isFetchRecordDetailLoader ||
              lockParticipantFromCalendarCell ||
              (userItem && calendarView === "participant-view")
            }
            persistSelectedOption={true}
            preserveSelectionWhenOptionMissing={true}
            cssClass="z-30"
            showIcon={true}
            isSearch={true}
            data={participantOptions}
            isForRostering={true}
            selectedOption={
              getSelectedParticipant("pt_id") ||
              shiftRequestParticipantFallback ||
              (userItem &&
              watchFormData?.pt_id &&
              String(userItem.uuid || userItem.value) ===
                String(watchFormData.pt_id)
                ? {
                    ...userItem,
                    uuid: userItem.uuid || userItem.value,
                    value: userItem.uuid || userItem.value,
                    label:
                      userItem.label ||
                      `${userItem.first_name || ""} ${
                        userItem.last_name || ""
                      }`.trim(),
                  }
                : null)
            }
            errorMessage={
              errors?.pt_id?.type === "required"
                ? "This field is required!"
                : null
            }
          />
        ))}
    </div>
  );
};

export default ParticipantSelection;
